import React, { useState, useEffect, useCallback } from 'react';
import './AMFacilities.css';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/ServicesSection';
import Results from './components/Results';
import HowItWorks from './components/HowItWorks';
import AboutMe from './components/AboutMe';
import About from './components/About';
import Contact from './components/Contact';
import Testimonials from './components/Testimonials';
import Terms from './components/Terms';
import TermsFull from './components/TermsFull';
import HomeMaintenance from './pages/HomeMaintenance';
import CondoCleaning from './pages/CondoCleaning';
import HouseCleaning from './pages/HouseCleaning';
import OfficeCleaning from './pages/OfficeCleaning';
import Footer from './components/Footer';
import Seo from './components/Seo';
import LocalBusinessJsonLd from './components/LocalBusinessJsonLd';

import QuoteCalculator from './components/QuoteCalculator';

export default function AMFacilities() {
  const [showQuote, setShowQuote] = useState(false);
  const [route, setRoute] = useState(window.location.pathname || '/');

  const handleShowQuote = useCallback(() => {
    setShowQuote(true);
    // Scroll to quote calculator smoothly - use scrollIntoView which respects scroll-margin-top
    setTimeout(() => {
      const el = document.getElementById('quote-calculator');
      if (!el) {
        console.log('Quote calculator element not found');
        return;
      }
      console.log('Scrolling to quote calculator');
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200); // Increased timeout to ensure component has fully rendered
  }, []);

  const handleCloseQuote = () => {
    setShowQuote(false);
  };

  // Delegate clicks on the page to open the quote calculator when relevant
  const delegatedClickHandler = useCallback((e) => {
    // Find the nearest anchor/button from the click target
    let el = e.target;
    
    // Walk up the DOM tree to find a button or anchor
    while (el && el !== document.body) {
      if (el.tagName === 'BUTTON' || el.tagName === 'A') {
        break;
      }
      el = el.parentElement;
    }
    
    if (!el || (el.tagName !== 'BUTTON' && el.tagName !== 'A')) return;

    // Debug logging
    const text = (el.textContent || '').toLowerCase().trim();
    console.log('Button/Link clicked:', {
      tagName: el.tagName,
      text: text,
      className: el.className,
      href: el.href
    });

    // Match by explicit attributes or classes
    if (el.matches('[href="#quote"], [data-open-quote], .js-open-quote')) {
      e.preventDefault();
      console.log('Matched by attribute/class');
      handleShowQuote();
      return;
    }

    // Match by visible text for common CTAs (case-insensitive)
    if (
      text.includes('request a free quote') ||
      text.includes('i want some results') ||
      text.includes('i want some results like this')
    ) {
      e.preventDefault();
      console.log('Matched by text:', text);
      handleShowQuote();
    }
  }, [handleShowQuote]);

  useEffect(() => {
    document.addEventListener('click', delegatedClickHandler);
    return () => document.removeEventListener('click', delegatedClickHandler);
  }, [delegatedClickHandler]);

  // Simple client-side navigation: intercept internal links and update route
  useEffect(() => {
    const handler = (e) => {
      // Only handle same-origin internal links
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || a.target === '_blank') return;
      // If it's an internal path we want to handle, prevent full page reload
      if (href.startsWith('/')) {
        e.preventDefault();
        // Extract hash from href (e.g., "/#services" -> "#services")
        const hashMatch = href.match(/#(.+)$/);
        const hash = hashMatch ? hashMatch[0] : '';
        const path = href.replace(hash, '') || '/';

        window.history.pushState({}, '', href);
        setRoute(path);

        // If there's a hash, scroll to it after route updates
        if (hash) {
          // First scroll to top to reset position
          window.scrollTo({ top: 0, behavior: 'auto' });

          setTimeout(() => {
            const element = document.querySelector(hash);
            if (element) {
              // Get the first child with content (heading, text, etc) within the section
              const firstContent =
                element.querySelector('h1, h2, h3, .am-hero-title, .am-results-title, .am-section-title') || element;
              const header = document.querySelector('.am-header');
              const headerHeight = header ? header.getBoundingClientRect().height : 0;
              const gap = 100; // larger spacing to ensure content is visible
              const elementPosition = firstContent.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - headerHeight - gap;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }
          }, 200);
        } else {
          // No hash, scroll to top of page for regular page navigation
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };
    document.addEventListener('click', handler);
    window.addEventListener('popstate', () => setRoute(window.location.pathname));
    return () => {
      document.removeEventListener('click', handler);
      window.removeEventListener('popstate', () => setRoute(window.location.pathname));
    };
  }, []);

  return (
    <div className='am-root'>
      <Seo pathname={route} />
      <Header />
      <main className='am-container'>
        {route === '/home-maintenance' ? (
          <HomeMaintenance />
        ) : route === '/condo-cleaning' ? (
          <CondoCleaning />
        ) : route === '/house-cleaning' ? (
          <HouseCleaning />
        ) : route === '/office-cleaning' ? (
          <OfficeCleaning />
        ) : route === '/terms' ? (
          <TermsFull />
        ) : (
          <>
            <LocalBusinessJsonLd />
            <Hero onShowQuote={handleShowQuote} />
            {showQuote && (
              <div id='quote-calculator' className='quote-calculator-wrapper'>
                <button className='quote-close-btn' onClick={handleCloseQuote} aria-label='Close quote calculator'>
                  ×
                </button>
                <QuoteCalculator />
              </div>
            )}

            <Results />
            <Services />
            <HowItWorks />
            <AboutMe />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
